import { DashboardCard } from "./dashboard-card";
import { useAccountContext } from "../account-context";
import { usePnlHistory } from "../hooks/use-pnl-history";
import { formatCurrency, cn } from "@/lib/utils";
import { Spinner } from "@/components/spinner";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { Area, AreaChart, XAxis, YAxis, CartesianGrid } from "recharts";
import { TrendingUpIcon, TrendingDownIcon, BarChart3Icon } from "lucide-react";
import { type DateRangeValue } from "@/components/ui/date-range-selector";

const chartConfig: ChartConfig = {
  cumulativeNetPnl: {
    label: "Net P&L",
    color: "var(--color-bid-foreground)",
  },
};

type PnlChartCardProps = {
  dateRange?: DateRangeValue;
};

export const PnlChartCard = ({ dateRange: _dateRange }: PnlChartCardProps) => {
  const { fills, fundingPayments, isLoading } = useAccountContext();
  const { pnlHistory, summary } = usePnlHistory({ fills, fundingPayments });

  if (isLoading) {
    return (
      <DashboardCard title="P&L History" subtitle="Cumulative profit and loss over time" className="h-full">
        <div className="flex items-center justify-center h-80">
          <Spinner />
        </div>
      </DashboardCard>
    );
  }

  if (!pnlHistory.length) {
    return (
      <DashboardCard title="P&L History" subtitle="Cumulative profit and loss over time" className="h-full">
        <div className="flex flex-col items-center justify-center h-80 text-muted-foreground">
          <BarChart3Icon className="size-12 mb-3 opacity-50" />
          <p className="text-sm">No trading history yet</p>
          <p className="text-xs">Start trading to see your P&L chart</p>
        </div>
      </DashboardCard>
    );
  }

  const isPositive = summary.totalNetPnl >= 0;
  const gradientColor = isPositive ? "var(--color-bid-foreground)" : "var(--color-ask-foreground)";

  // Format data for chart
  const chartData = pnlHistory.map((point) => ({
    ...point,
    dateLabel: new Date(point.date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }),
  }));

  return (
    <DashboardCard
      title="P&L History"
      subtitle="Cumulative profit and loss over time"
      className="h-full"
      action={
        <div
          className={cn(
            "flex items-center gap-1 text-sm font-semibold px-2 py-1 rounded-md",
            isPositive ? "text-bid-foreground bg-bid" : "text-ask-foreground bg-ask"
          )}
        >
          {isPositive ? (
            <TrendingUpIcon className="size-4" />
          ) : (
            <TrendingDownIcon className="size-4" />
          )}
          <span>
            {isPositive ? "+" : ""}${formatCurrency(Math.abs(summary.totalNetPnl), 2)}
          </span>
        </div>
      }
    >
      <div className="h-80">
        <ChartContainer config={chartConfig} className="h-full w-full">
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="pnlGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={gradientColor} stopOpacity={0.3} />
                <stop offset="100%" stopColor={gradientColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="var(--border)"
            />
            <XAxis
              dataKey="dateLabel"
              tickLine={false}
              axisLine={false}
              tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
              tickMargin={8}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
              tickFormatter={(value) => `$${value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toFixed(0)}`}
              tickMargin={8}
              width={60}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  formatter={(value) => (
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-muted-foreground">Net P&L</span>
                      <span
                        className={cn(
                          "font-semibold",
                          Number(value) >= 0 ? "text-bid-foreground" : "text-ask-foreground"
                        )}
                      >
                        {Number(value) >= 0 ? "+" : ""}${formatCurrency(Math.abs(Number(value)), 2)}
                      </span>
                    </div>
                  )}
                />
              }
            />
            <Area
              type="monotone"
              dataKey="cumulativeNetPnl"
              stroke={gradientColor}
              strokeWidth={2}
              fill="url(#pnlGradient)"
            />
          </AreaChart>
        </ChartContainer>
      </div>
    </DashboardCard>
  );
};
